<template>
  <div class="buttons">
    <ButtonElement
      v-if="!showForm"
      color="success"
      :icon="PhPlus"
      bordered
      @click="openProduitFiltration(undefined)"
      >Ajouter un produit de filtration</ButtonElement
    >
    <ButtonElement v-else color="bordeaux" bordered :icon="PhArrowLeft" @click="showForm = false">
      Retour
    </ButtonElement>
  </div>
  <!-- Formulaire de création/modification -->
  <OrdreFabricationProduitFiltrationForm
    v-if="showForm"
    :categories
    :fabricants
    :produits
    v-model:ordre-fabrication="ordreFabrication"
    v-model:produit="produit"
    v-model:show-form="showForm"
  />

  <!-- Listing -->
  <template v-else>
    <table class="table" v-if="ordreFabrication.produits_filtration.length">
      <thead>
        <tr>
          <th class="th">Produit</th>
          <th class="th">Fabricant</th>
          <th class="th">N° de lot</th>
          <th class="th">N° de semaine</th>
          <th class="th">DLUO</th>
          <th class="th">Actions</th>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="(produitFiltration, index) in ordreFabrication.produits_filtration"
          :key="produitFiltration.produit_filtration"
        >
          <tr class="tr">
            <td
              v-if="
                index === 0 ||
                getCategorieNom(
                  ordreFabrication.produits_filtration[index - 1]?.produit_filtration,
                ) !== getCategorieNom(produitFiltration.produit_filtration)
              "
              class="td font-style__italic"
              colspan="6"
            >
              {{ getCategorieNom(produitFiltration.produit_filtration) }}
            </td>
          </tr>
          <tr class="tr">
            <td class="td">{{ getProduitFiltrationNom(produitFiltration.produit_filtration) }}</td>
            <td class="td">{{ getFabricantNom(produitFiltration.fabricant) }}</td>
            <td class="td">{{ produitFiltration.numero_lot }}</td>
            <td class="td">{{ produitFiltration.numero_semaine }}</td>
            <td class="td">{{ frenchDate(produitFiltration.date_limite_utilisation_optimale) }}</td>
            <td class="td">
              <div class="buttons">
                <ButtonElement
                  size="small"
                  color="bordeaux"
                  :icon="PhPen"
                  title="Modifier ce produit de filtration"
                  @click="openProduitFiltration(produitFiltration)"
                />
                <ButtonElement
                  size="small"
                  color="danger"
                  bordered
                  :icon="PhTrash"
                  title="Supprimer ce produit de filtration"
                  @click="confirmDeleteProduitFiltration(produitFiltration)"
                />
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div class="message" v-else>
      <PhWarning />
      <span>Aucun produit de filtration n'est enregistré.</span>
    </div>
  </template>
  <ConfirmElement ref="confirmRef" @yes="deleteProduitFiltration" />
</template>

<script setup lang="ts">
import { PhArrowLeft, PhPen, PhPlus, PhTrash, PhWarning } from '@phosphor-icons/vue'
import type {
  OrdreFabricationGet,
  OrdreFabricationProduitFiltration,
} from '@/interfaces/OrdreFabrication.ts'
import type {
  ProduitFiltrationList,
  ProduitFiltrationCategorie,
} from '@/interfaces/ProduitFiltrationList.ts'
import { onMounted, type PropType, type Ref, ref, toRaw, useTemplateRef } from 'vue'
import type FabricantList from '@/interfaces/Fabricant.ts'
import ButtonElement from '@/components/ButtonElement.vue'
import frenchDate from '@/mixins/getFrenchDate.ts'
import { useCacheStore } from '@/stores/cache.ts'
import ConfirmElement from '@/components/ConfirmElement.vue'
import { useToastStore } from '@/stores/toast.ts'
import OrdreFabricationProduitFiltrationForm from '@/components/ordre-fabrication/OrdreFabricationProduitFiltrationForm.vue'

// modèle
const ordreFabrication = defineModel<OrdreFabricationGet>({ required: true })

// props
const props = defineProps({
  produits: {
    type: Array as PropType<ProduitFiltrationList[]>,
    required: true,
  },
})

// stores
const cacheStore = useCacheStore()
const toastStore = useToastStore()

// données
const fabricants: Ref<FabricantList[]> = ref([])

const produit: Ref<OrdreFabricationProduitFiltration> = ref({
  produit_filtration: '',
  fabricant: '',
  numero_lot: '',
  numero_semaine: '',
  date_limite_utilisation_optimale: '',
})
const categories: Ref<ProduitFiltrationCategorie[]> = ref([])
const showForm: Ref<boolean> = ref(false)

// refs
const confirmRef = useTemplateRef<typeof ConfirmElement>('confirmRef')

// fonctions
async function getCategories() {
  categories.value = await cacheStore.getProduitsCategories()
}

async function getFabricants() {
  fabricants.value = await cacheStore.getFabricants()
}
function getProduitFiltrationNom(produitId: string) {
  return props.produits.find((produit) => produit.id === produitId)?.nom
}
function getFabricantNom(fabricantId: string) {
  return fabricants.value.find((fabricant) => fabricant.id === fabricantId)?.nom
}
function getCategorieNom(produitFiltrationId: string | undefined) {
  return produitFiltrationId
    ? categories.value.find((categorie) =>
        categorie.produits_filtration
          .map((produitFiltration) => produitFiltration.id)
          .includes(produitFiltrationId),
      )?.nom
    : ''
}
// gestion des produits de filtration
function openProduitFiltration(
  produitFiltration: OrdreFabricationProduitFiltration | undefined = undefined,
) {
  // ouvre le formulaire de modification avec le produit de filtration choisi
  if (produitFiltration) produit.value = structuredClone(toRaw(produitFiltration))
  else {
    produit.value = {
      produit_filtration: '',
      fabricant: '',
      numero_lot: '',
      numero_semaine: null,
      date_limite_utilisation_optimale: '',
    }
  }
  showForm.value = true
}

function confirmDeleteProduitFiltration(produitFiltration: OrdreFabricationProduitFiltration) {
  confirmRef.value?.show(
    produitFiltration,
    "Suppression d'un produit de filtration",
    'Êtes-vous sûr de vouloir supprimer ce produit de filtration ?',
  )
}
function deleteProduitFiltration(produitFiltration: OrdreFabricationProduitFiltration) {
  const index = ordreFabrication.value.produits_filtration.indexOf(produitFiltration)
  ordreFabrication.value.produits_filtration.splice(index, 1)
  toastStore.success('Le produit de filtration a bien été supprimé.')
}

onMounted(async () => {
  await getFabricants()
  await getCategories()
})
</script>
